import type { DashboardComponent } from 'common/types/dashboard/dashboardDefinition';
import { getValueType } from 'common/types/summaryDefinitions';

// Exhaustive by construction: adding a component type to the backend registry regenerates
// DashboardComponent and this record stops compiling until the new type gets a label.
export const COMPONENT_TYPE_LABELS: Record<DashboardComponent['type'], string> = {
  bar_chart: 'Bar chart',
  content_card: 'Content',
  line_chart: 'Line chart',
  list: 'Ranked list',
  pie_chart: 'Pie chart',
  table: 'Table',
  totals: 'Totals',
};

const humanizeMetric = (metric: string): string =>
  metric
    .split('_')
    .map(word => (word === 'moic' || word === 'irr' ? word.toUpperCase() : word))
    .join(' ');

// The schema allows these two as chart dimensions but they are not summary columns, so the
// value-type registry carries no entry to label them with.
const SPECIAL_DIMENSION_LABELS: Record<string, string> = {
  financial_period: 'Financial period',
  measurement_date: 'Measurement date',
};

const sentenceCase = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

// Prefers the value-type registry so a column is named exactly as its spreadsheet header names it
// ("Total Value"), with the humanized form as the fallback for names the registry does not carry.
// The tile chips below deliberately keep the humanized form on its own — see describeComponentFields.
export const resolveColumnLabel = (name: string): string =>
  getValueType(name)?.displayName ?? SPECIAL_DIMENSION_LABELS[name] ?? sentenceCase(humanizeMetric(name));

// Turns each column name into a short human label so we can show the metric/dimension selections
// on the card, which is the only way to tell whether the AI picked the right fields while there is
// still no data endpoint behind the tiles.
export const describeComponentFields = (component: DashboardComponent): string[] => {
  switch (component.type) {
    case 'totals':
      return component.fields.metrics.map(humanizeMetric);
    case 'bar_chart':
    case 'line_chart':
      return [
        humanizeMetric(component.fields.dimension),
        ...component.fields.metrics.map(humanizeMetric),
        ...(component.fields.series ?? []).map(humanizeMetric),
      ];
    case 'pie_chart':
      return [humanizeMetric(component.fields.dimension), humanizeMetric(component.fields.metric)];
    case 'list':
      return [
        humanizeMetric(component.fields.metric),
        `top ${component.fields.limit ?? 5} by ${component.fields.entity_kind}`,
      ];
    case 'table': {
      // Either branch of the table union carries an index-keyed column map; the embedded
      // spreadsheet is the shape the backend normalizes everything to.
      const columns =
        'spreadsheet_definition' in component.fields
          ? component.fields.spreadsheet_definition.columns
          : component.fields.columns;
      // Read as unknown despite the generated `string` type. The JSON Schema documents the embedded
      // spreadsheet as a structural hint with the Python validator authoritative, and rows written
      // before that validator rejected non-string column values still hold objects like
      // {"0": {"metric": "moic"}} — which crashed this card. The other cases are safe to trust:
      // metric/metrics/dimension/series are all vocabulary-checked strings on save.
      return Object.values<unknown>(columns ?? {})
        .filter((value): value is string => typeof value === 'string')
        .map(humanizeMetric);
    }
    case 'content_card':
      return component.fields?.metric ? [humanizeMetric(component.fields.metric)] : [];
    default:
      return [];
  }
};
